import { cilFilter } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { CButton, CCard, CCardBody, CContainer, CForm, CFormCheck, CFormInput } from '@coreui/react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';
import { v4 as uuidv4 } from 'uuid';

import {
    generateAppointmentCheckinData,
    generateCode,
    updateUserByNumber,
} from '@/api/appointment/AppointmentCalls.js';
import { getFormTemplates } from '@/api/professional/ProfessionalCalls.js';

import Alert from '@/components/Alert.jsx';
import AppointmentCodeModal from '@/components/TodaysAppointments/AppointmentCodeModal.jsx';
import AppointmentDetails from '@/components/TodaysAppointments/AppointmentDetails.jsx';
import AppointmentEntry from '@/components/TodaysAppointments/AppointmentEntry.jsx';
import CertModal from '@/components/TodaysAppointments/CertModal.jsx';
import GlobalManualCheckinModal from '@/components/TodaysAppointments/GlobalManualCheckinModal.jsx';
import LegacyCheckinModal from '@/components/TodaysAppointments/LegacyCheckinModal.jsx';
import LegacyManualCheckinModal from '@/components/TodaysAppointments/LegacyManualCheckinModal.jsx';
import LoadingIndicator from '@/components/TodaysAppointments/LoadingIndicator.jsx';
import ManualCheckinModal from '@/components/TodaysAppointments/ManualCheckinModal.jsx';
import PrinterDropdown from '@/components/TodaysAppointments/PrinterDropdown.jsx';

import AWSContext from '@/context/AWSContext.jsx';
import AppointmentContext from '@/context/appointment/AppointmentContext.js';

import { UserStatus } from '@/utils/constants.js';
import {
    AppointmentStatus,
    FeatureKey,
    PVSProvider,
    getExtAuthToken,
    getUnknownAppointments,
    loadCachedUserData,
    printPcsTicket,
    updatePcs,
} from '@/utils/helpers.js';

import {
    useAdminConfigStore,
    useLocationStore,
    usePatientCallSystemStore,
    usePowerUserStore,
    usePrismicStore,
    useProfessionalsStore,
    useTodaysAppointmentsStore,
} from '@/zustandStore.js';

import FailedFallback from '../pages/failed/FailedFallback.jsx';
import LoadingPage from '../pages/loading/LoadingPage.jsx';

const TodaysAppointments = () => {
    const { userToken } = useContext(AWSContext);
    const { t } = useTranslation();
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    // TODO: toggle
    const [showUnknownAppointments] = useState(true);
    const [showPastAppointments, setShowPastAppointments] = useState(false);
    const [showAppointmentsWithoutProfessional, setShowAppointmentsWithoutProfessional] = useState(false);
    const [manualCheckinPatientName, setManualCheckinPatientName] = useState('');
    const [showFilterSection, setShowFilterSection] = useState(false);
    const [professionalsFilter, setProfessionalsFilter] = useState({});
    const [loading, setLoading] = useState(true);
    const {
        showLegacyCheckinModal,
        showNewManualCheckinModal,
        showLegacyManualCheckinModal,
        showGlobalManualCheckinModal,
        todaysAppointments: savedTodaysAppointments,
        unknownAppointments,
        hasFailed,
        appointmentTicketNumber,
        checkinData,
    } = useTodaysAppointmentsStore();
    const prismicData = usePrismicStore((state) => state.todaysAppointmentsPrismicData);
    const professionals = usePrismicStore((state) => state.professionals);
    const userGroups = usePrismicStore((state) => state.userGroups);
    const currentCustomer = usePrismicStore((state) => state.currentCustomer);
    const selectedLocation = useLocationStore((state) => state.selectedLocation);
    const selectedPrinter = usePatientCallSystemStore((state) => state.selectedPrinter);
    const featureAccess = useAdminConfigStore((state) => state.featureAccess);
    const cachedUserData = useProfessionalsStore((state) => state.cachedUserData);
    const professionalMappings = useProfessionalsStore((state) => state.professionalMappings);
    const { getAllTodaysAppointments, todaysAppointments, error, setToDefault, successMessage } =
        useContext(AppointmentContext);

    useSWRImmutable(
        !cachedUserData || currentCustomer?.customer_id?.[0]?.text
            ? ['professionals', currentCustomer?.customer_id?.[0]?.text]
            : null,
        async () => loadCachedUserData(userToken)
    );

    const filteredAndSortedProfessionals = useMemo(() => {
        if (!professionals || !professionalMappings) return [];

        const instanceIdSet = new Set(selectedLocation?.instanceIds);
        const hubIdSet = new Set((selectedLocation?.configs ?? []).map((config) => config.hub.id));

        return Object.keys(professionals)
            .filter(
                (key) =>
                    instanceIdSet.has(professionalMappings[key]?.dc_instance_id) &&
                    hubIdSet.has(professionals[key].location_hub.id)
            )
            .sort((a, b) => professionals[a].display_name[0].text.localeCompare(professionals[b].display_name[0].text));
    }, [professionals, professionalMappings, selectedLocation]);

    const updateSelectedAppointment = (item) => {
        const { professionalFormTemplates, isCheckinDataLoading } = useTodaysAppointmentsStore.getState();

        if (detailsLoading || !professionalFormTemplates || isCheckinDataLoading) return;

        if (item.id === selectedAppointment?.id) {
            setSelectedAppointment(null);
        } else {
            setSelectedAppointment(item);
            setDetailsLoading(true);
        }
    };

    const updateProfessionalsFilter = (professionalKey) => {
        const newProfessionalsFilter = { ...professionalsFilter };

        if (newProfessionalsFilter[professionalKey]) {
            delete newProfessionalsFilter[professionalKey];
        } else {
            newProfessionalsFilter[professionalKey] = true;
        }

        setProfessionalsFilter(newProfessionalsFilter);
    };

    const loadAuthToken = async (instanceId) => {
        const { authTokens } = usePatientCallSystemStore.getState();

        const authToken = authTokens[instanceId];

        if (!authToken?.token || Date.now() > authToken.validUntil) {
            const newToken = await getExtAuthToken(userToken, instanceId);
            if (!newToken) {
                return null;
            }

            const newAuthToken = {
                token: newToken,
                validUntil: Date.now() + 3600000,
                instanceId: instanceId,
            };

            usePatientCallSystemStore.setState({ authTokens: { ...authTokens, [instanceId]: newAuthToken } });
        }

        if (!authToken?.token) {
            return null;
        }

        if (userGroups?.admin) {
            usePowerUserStore.setState({
                lastUsedCert: selectedPrinter[selectedLocation.key]?.certificate[0].text,
            });
        }

        return authToken;
    };

    const prepareGenerateCodeBody = (selectedForms, appointmentId = null, selectedProfessional = null) => {
        const getUserId = () => {
            if (selectedAppointment && !appointmentId) {
                if (selectedAppointment.user_id && selectedAppointment.user_id !== '-') {
                    return selectedAppointment.user_id;
                }
                return selectedAppointment.links.patient_id ?? '-';
            }
            return '-';
        };

        return {
            forms: selectedForms ?? [],
            location: {
                location_key: selectedLocation.key,
            },
            user: {
                user_id: getUserId(),
            },
            professional: {
                professional_id:
                    selectedAppointment && !selectedProfessional
                        ? selectedAppointment.professional.id
                        : professionalMappings[selectedProfessional].user_id,
                professional_key:
                    selectedAppointment && !selectedProfessional
                        ? selectedAppointment.professional.key
                        : professionalMappings[selectedProfessional].prismic_key,
            },
            appointment_id: appointmentId ?? selectedAppointment.id,
        };
    };

    const updateCheckin = async (status, ticket = null, selectedProfessional = null) => {
        const data = {
            group: selectedLocation.key,
            number: ticket ? String(ticket) : appointmentTicketNumber[selectedAppointment.id],
            name:
                selectedAppointment.data.patient_name ||
                (selectedAppointment.patient?.name.first_name
                    ? `${selectedAppointment.patient?.name?.first_name} ${selectedAppointment.patient?.name?.last_name}`
                    : manualCheckinPatientName),
            user_id:
                selectedAppointment.user_id && selectedAppointment.user_id !== '-'
                    ? selectedAppointment.user_id
                    : selectedAppointment.data.generated_id,
            location_key: selectedLocation.key,
            appointment: {
                id: selectedAppointment.id,
                checkin_status: status,
                doctor_name: selectedProfessional ?? '',
            },
            instance_id: selectedAppointment.links.dc_instance_id,
        };

        const response = await updateUserByNumber(userToken, data);

        if (!response.data) return;

        useTodaysAppointmentsStore.setState({
            checkinData: { ...checkinData, [selectedAppointment.id]: response.data },
        });
    };

    const confirmCheckinForUnknownAppointment = async () => {
        const data = {
            group: selectedLocation.key,
            number: checkinData[selectedAppointment.id].number,
            name: checkinData[selectedAppointment.id].name,
            user_id: '-',
            location_key: selectedLocation.key,
            user_status: UserStatus.UNKNOWN,
            appointment: {
                id: selectedAppointment.id,
                checkin_status: AppointmentStatus.MANUALLY_CHECKED_IN,
                doctor_name: checkinData[selectedAppointment.id].appointment.doctor_name,
            },
            instance_id: selectedAppointment.links?.dc_instance_id ?? selectedAppointment.instance_id,
        };

        const response = await updateUserByNumber(userToken, data);

        if (!response.data) return;

        useTodaysAppointmentsStore.setState({
            checkinData: { ...checkinData, [selectedAppointment.id]: response.data },
        });
    };

    const confirmCheckin = async () => {
        if (!checkinData[selectedAppointment.id]?.number && !appointmentTicketNumber[selectedAppointment.id]) return;

        const instance_id = selectedAppointment.links?.dc_instance_id ?? selectedAppointment.instance_id;

        const config = selectedLocation.configs.find((c) => c.instance_id[0].text === instance_id);
        if (!config) return;

        setDetailsLoading(true);

        let authToken;
        if (config.is_new_checkin_available) {
            authToken = await loadAuthToken(instance_id);

            if (!authToken) {
                setDetailsLoading(false);
                return;
            }
        }

        const isManualCheckin = !selectedAppointment.data?.patient_name || !selectedAppointment.patient?.name;

        if (selectedAppointment.user_status === UserStatus.UNKNOWN) {
            await confirmCheckinForUnknownAppointment();
            setDetailsLoading(false);
            return;
        }

        if (config.is_new_checkin_available) {
            await updatePcs(
                authToken.token,
                selectedPrinter[selectedLocation.key]?.address[0].text ??
                    config.pcs_printers?.[0]?.address[0].text ??
                    config.patient_call_system_ip[0].text,
                selectedPrinter[selectedLocation.key]?.certificate[0].text ??
                    config.pcs_printers?.[0]?.certificate[0].text,
                professionalMappings[selectedAppointment.professional.key]?.pcs_stack_id || 'zimmer01',
                checkinData[selectedAppointment.id]?.number ?? appointmentTicketNumber[selectedAppointment.id]
            );
        }

        await updateCheckin(
            isManualCheckin ? AppointmentStatus.MANUALLY_CHECKED_IN : AppointmentStatus.CHECKED_IN,
            checkinData[selectedAppointment.id]?.number ?? appointmentTicketNumber[selectedAppointment.id],
            checkinData[selectedAppointment.id].appointment.doctor_name
        );

        setDetailsLoading(false);
    };

    const startCheckin = async (selectedProfessional = null) => {
        const config = selectedLocation.configs.find(
            (c) => c.instance_id[0].text === selectedAppointment.links.dc_instance_id
        );
        if (!config) return;

        setDetailsLoading(true);

        const authToken = await loadAuthToken(selectedAppointment.links.dc_instance_id);

        if (!authToken) {
            setDetailsLoading(false);
            return;
        }

        const ticket = await printPcsTicket(
            authToken.token,
            selectedPrinter[selectedLocation.key]?.address[0].text ??
                config.pcs_printers?.[0]?.address[0].text ??
                config.patient_call_system_ip[0].text,
            selectedPrinter[selectedLocation.key]?.certificate[0].text ?? config.pcs_printers?.[0]?.certificate[0].text
        );
        if (!ticket) {
            setDetailsLoading(false);
            return;
        }

        useTodaysAppointmentsStore.setState({
            appointmentTicketNumber: {
                ...appointmentTicketNumber,
                [selectedAppointment.id]: ticket,
            },
        });

        if (userGroups?.admin) {
            usePowerUserStore.setState({
                lastUsedCert:
                    selectedPrinter[selectedLocation.key]?.certificate[0].text ??
                    config.pcs_printers?.[0]?.certificate[0].text,
            });
        }

        await updateCheckin(AppointmentStatus.ARRIVED, ticket, selectedProfessional);

        const isManualCheckin = !(selectedAppointment.data?.patient_name || selectedAppointment.patient?.name);
        const selectedForms = useTodaysAppointmentsStore.getState().selectedForms[selectedAppointment.id];

        if (!featureAccess[FeatureKey.CONSENT_FORMS] || !selectedForms || selectedForms.length === 0) {
            const responseStatus = await updatePcs(
                authToken.token,
                selectedPrinter[selectedLocation.key]?.address[0].text ??
                    config.pcs_printers?.[0]?.address[0].text ??
                    config.patient_call_system_ip[0].text,
                selectedPrinter[selectedLocation.key]?.certificate[0].text ??
                    config.pcs_printers?.[0]?.certificate[0].text,
                professionalMappings[selectedAppointment.professional.key]?.pcs_stack_id || 'zimmer01',
                ticket
            );

            if (!responseStatus || responseStatus < 200 || responseStatus >= 300) {
                setDetailsLoading(false);
                return;
            }

            await updateCheckin(
                isManualCheckin ? AppointmentStatus.MANUALLY_CHECKED_IN : AppointmentStatus.CHECKED_IN,
                ticket,
                selectedProfessional
            );

            setDetailsLoading(false);
            return;
        }

        const generateCodeBody = prepareGenerateCodeBody(selectedForms);

        const generatedCode = await generateCode(userToken, generateCodeBody);
        await updateCheckin(AppointmentStatus.ARRIVED, ticket, selectedProfessional);

        useTodaysAppointmentsStore.setState({ code: generatedCode });
        setDetailsLoading(false);
    };

    const manualCheckin = async (selectedProfessional) => {
        if (!selectedProfessional || !manualCheckinPatientName) {
            return;
        }
        await startCheckin(selectedProfessional);
    };

    const startLegacyCheckin = async () => {
        const config = selectedLocation.configs.find(
            (c) => c.instance_id[0].text === selectedAppointment.links.dc_instance_id
        );
        if (!config) return;

        setDetailsLoading(true);
        const ticket = config.is_patient_call_system_active ? null : uuidv4();

        const isManualCheckin = !(selectedAppointment.data?.patient_name || selectedAppointment.patient?.name);
        const selectedForms = useTodaysAppointmentsStore.getState().selectedForms[selectedAppointment.id];

        if (!featureAccess[FeatureKey.CONSENT_FORMS] || !selectedForms || selectedForms.length === 0) {
            await updateCheckin(
                isManualCheckin ? AppointmentStatus.MANUALLY_CHECKED_IN : AppointmentStatus.CHECKED_IN,
                ticket
            );
            setDetailsLoading(false);
            return;
        }

        const generateCodeBody = prepareGenerateCodeBody(selectedForms);

        await updateCheckin(AppointmentStatus.ARRIVED, ticket);
        const generatedCode = await generateCode(userToken, generateCodeBody);
        useTodaysAppointmentsStore.setState({ code: generatedCode });
        setDetailsLoading(false);
    };

    const globalManualCheckin = async (selectedProfessional, appointmentId) => {
        // TODO: add instance selection dropdown to global manual checkin
        const config = selectedLocation.dcConfig;
        if (!config) return;

        setLoading(true);
        const selectedForms = useTodaysAppointmentsStore.getState().selectedForms[appointmentId];
        const { usePcsForGlobalManualCheckin } = useTodaysAppointmentsStore.getState();

        let ticket = config.is_patient_call_system_active ? null : uuidv4();
        let authToken = null;
        if (usePcsForGlobalManualCheckin) {
            authToken = await loadAuthToken();

            if (authToken) {
                ticket = await printPcsTicket(
                    authToken.token,
                    selectedPrinter[selectedLocation.key]?.address[0].text ??
                        config.pcs_printers?.[0]?.address[0].text ??
                        config.patient_call_system_ip[0].text,
                    selectedPrinter[selectedLocation.key]?.certificate[0].text ??
                        config.pcs_printers?.[0]?.certificate[0].text
                );

                if (ticket) {
                    useTodaysAppointmentsStore.setState({
                        appointmentTicketNumber: {
                            ...appointmentTicketNumber,
                            [appointmentId]: ticket,
                        },
                    });
                }
            }
        }

        if (!ticket && !appointmentTicketNumber.global) {
            setLoading(false);
            return;
        }

        const data = {
            group: selectedLocation.key,
            number: ticket ?? appointmentTicketNumber.global,
            name: manualCheckinPatientName,
            user_id: '-',
            location_key: selectedLocation.key,
            user_status: UserStatus.UNKNOWN,
            appointment: {
                id: appointmentId,
                checkin_status:
                    !featureAccess[FeatureKey.CONSENT_FORMS] || !selectedForms || selectedForms.length === 0
                        ? AppointmentStatus.MANUALLY_CHECKED_IN
                        : AppointmentStatus.ARRIVED,
                doctor_name: selectedProfessional ?? '',
            },
            instance_id: config.instance_id[0].text,
        };

        const response = await updateUserByNumber(userToken, data);
        if (!response.data) {
            setLoading(false);
            return;
        }

        if (featureAccess[FeatureKey.CONSENT_FORMS] && selectedForms?.length > 0) {
            const generateCodeBody = prepareGenerateCodeBody(selectedForms, appointmentId, selectedProfessional);

            const generatedCode = await generateCode(userToken, generateCodeBody);
            useTodaysAppointmentsStore.setState({ code: generatedCode });
        } else if (usePcsForGlobalManualCheckin && authToken) {
            await updatePcs(
                authToken.token,
                selectedPrinter[selectedLocation.key]?.address[0].text ??
                    config.pcs_printers?.[0]?.address[0].text ??
                    config.patient_call_system_ip[0].text,
                selectedPrinter[selectedLocation.key]?.certificate[0].text ??
                    config.pcs_printers?.[0]?.certificate[0].text,
                professionalMappings[selectedProfessional]?.pcs_stack_id || 'zimmer01',
                ticket ?? appointmentTicketNumber.global
            );
        }

        const appointmentObj = {
            ...response.data,
            id: response.data.appointment_id,
        };

        const newCheckinData = { ...useTodaysAppointmentsStore.getState().checkinData };
        newCheckinData[response.data.appointment_id] = appointmentObj;

        useTodaysAppointmentsStore.setState({
            checkinData: newCheckinData,
            unknownAppointments: [...unknownAppointments, appointmentObj],
        });
        setLoading(false);

        updateSelectedAppointment(appointmentObj);
    };

    const loadUnknownAppointments = async () => {
        const data = await getUnknownAppointments(userToken, selectedLocation.key);
        if (!data) {
            useTodaysAppointmentsStore.setState({ unknownAppointments: [] });
            return;
        }

        useTodaysAppointmentsStore.setState({
            unknownAppointments: data.map((entry) => {
                return {
                    ...entry,
                    id: entry.appointment_id,
                };
            }),
        });
        const newCheckinData = { ...useTodaysAppointmentsStore.getState().checkinData };
        data.forEach((entry) => {
            newCheckinData[entry.appointment_id] = entry;
        });
        useTodaysAppointmentsStore.setState({ checkinData: newCheckinData });
    };

    const loadProfessionalFormTemplates = async (mappings) => {
        const formTemplatePromises = Object.values(mappings).map((professionalMapping) =>
            getFormTemplates(userToken, professionalMapping.user_id).then((result) => ({
                prismicKey: professionalMapping.prismic_key,
                data: result.data?.data ?? [],
            }))
        );

        const resolvedPromises = await Promise.all(formTemplatePromises);

        const formTemplatesObj = resolvedPromises.reduce((obj, professionalData) => {
            if (!professionalData.prismicKey) return obj;

            // eslint-disable-next-line no-param-reassign
            obj[professionalData.prismicKey] = professionalData.data.reduce((accumulator, template) => {
                // eslint-disable-next-line no-param-reassign
                accumulator[template.id] = template;
                return accumulator;
            }, {});

            return obj;
        }, {});

        useTodaysAppointmentsStore.setState({
            professionalFormTemplates: formTemplatesObj,
        });
    };

    const loadTodaysAppointments = () => {
        const currentDate = `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${new Date().getDate().toString().padStart(2, '0')}`;

        const instanceIds = (selectedLocation?.configs ?? [])
            .filter((config) => config.provider === PVSProvider.DOC_CIRRUS)
            .map((config) => config.instance_id[0].text);

        getAllTodaysAppointments(userToken, currentDate, instanceIds);
    };

    const loadCheckinData = async () => {
        try {
            const responses = await Promise.all(
                (selectedLocation?.instanceIds ?? []).map((instanceId) =>
                    generateAppointmentCheckinData(userToken, instanceId)
                )
            );

            const checkinDataObj = {};

            for (const response of responses) {
                for (const data of response) {
                    if (!data.appointment?.id) continue;
                    checkinDataObj[data.appointment.id] = data;
                }
            }

            useTodaysAppointmentsStore.setState({
                checkinData: { ...useTodaysAppointmentsStore.getState().checkinData, ...checkinDataObj },
                isCheckinDataLoading: false,
            });
        } catch (e) {
            if (e.status !== 404) {
                console.debug(e);
                return;
            }
            useTodaysAppointmentsStore.setState({
                isCheckinDataLoading: false,
            });
        }
    };

    const resetProfessionalsFilter = () => {
        const instanceIdSet = new Set(selectedLocation?.instanceIds);
        const hubIdSet = new Set((selectedLocation?.configs ?? []).map((config) => config.hub.id));

        setProfessionalsFilter(
            Object.keys(professionals)
                .filter(
                    (key) =>
                        instanceIdSet.has(professionalMappings[key]?.dc_instance_id) &&
                        hubIdSet.has(professionals[key].location_hub.id)
                )
                .reduce((obj, key) => {
                    // eslint-disable-next-line no-param-reassign
                    obj[key] = true;
                    return obj;
                }, {})
        );
    };

    const applyFilters = (appointments) => {
        let newFilteredAppointments = [...appointments];
        if (!showPastAppointments) {
            newFilteredAppointments = newFilteredAppointments.filter((appointment) => !appointment.inactive);
        }
        if (!showAppointmentsWithoutProfessional) {
            newFilteredAppointments = newFilteredAppointments.filter((appointment) => appointment.professional.key);
        }

        // Filter by professionalsFilter
        if (selectedLocation.dcConfig.is_filtering_active) {
            newFilteredAppointments = newFilteredAppointments.filter(
                (appointment) => professionalsFilter[appointment.professional.key]
            );
        }

        if (showUnknownAppointments) {
            const filteredUnknownAppointments = unknownAppointments.filter((entry) => {
                const isCorrectLocation = entry.location_key === selectedLocation.key;
                const isInProfessionalsFilterObj = selectedLocation.dcConfig.is_filtering_active
                    ? professionalsFilter[entry.appointment.doctor_name]
                    : true;
                return isCorrectLocation && isInProfessionalsFilterObj;
            });

            newFilteredAppointments.push(...filteredUnknownAppointments);

            newFilteredAppointments.sort(
                (a, b) =>
                    new Date(a.start ?? a.appointment?.checkin_time) - new Date(b.start ?? b.appointment?.checkin_time)
            );
        }
        return newFilteredAppointments;
    };

    useEffect(() => {
        if (professionals && professionalMappings) resetProfessionalsFilter();
    }, [professionals, professionalMappings, selectedLocation]);

    useEffect(() => {
        useLocationStore.setState({ loading: true });
        if (!savedTodaysAppointments && selectedLocation) loadTodaysAppointments();
        if (!unknownAppointments && selectedLocation) loadUnknownAppointments();
    }, []);

    useEffect(() => {
        if (professionalMappings) loadProfessionalFormTemplates(professionalMappings);
        setSelectedAppointment(null);
    }, [currentCustomer, professionalMappings]);

    useEffect(() => {
        if (loading || !savedTodaysAppointments) {
            return;
        }
        useTodaysAppointmentsStore.setState({
            todaysAppointments: undefined,
            isCheckinDataLoading: true,
            isTodaysAppointmentsLoading: true,
        });
        setLoading(true);
        setSelectedAppointment(null);
        setToDefault();
        loadTodaysAppointments();
        loadUnknownAppointments();
        useLocationStore.setState({ loading: true });
    }, [selectedLocation]);

    useEffect(() => {
        const setData = async () => {
            if (!successMessage || !professionals || !professionalMappings || !prismicData || !unknownAppointments) {
                return;
            }

            loadCheckinData();

            const sortedAppointments = [...todaysAppointments].sort((a, b) => new Date(a.start) - new Date(b.start));

            useTodaysAppointmentsStore.setState({
                todaysAppointments: sortedAppointments,
            });
            useLocationStore.setState({ loading: false });
            setLoading(false);
            setSearch('');
            setFilteredAppointments(applyFilters(sortedAppointments));
        };

        setData();
    }, [successMessage, professionals, professionalMappings, prismicData, unknownAppointments]);

    useEffect(() => {
        if (error) {
            useTodaysAppointmentsStore.setState({ hasFailed: true });
            setLoading(false);
        }
    }, [error]);

    const reloadData = async () => {
        setLoading(true);
        setSelectedAppointment(null);
        setToDefault();
        loadTodaysAppointments();
    };

    useEffect(() => {
        if (!savedTodaysAppointments) return;

        const newFilteredAppointments = applyFilters(savedTodaysAppointments);

        if (search) {
            setFilteredAppointments(
                newFilteredAppointments.filter((item) => {
                    let includesSearch = true;
                    search.split(' ').forEach((value) => {
                        if (
                            !(
                                item.patient?.name.first_name.toLowerCase().includes(value.toLowerCase()) ||
                                item.patient?.name.last_name.toLowerCase().includes(value.toLowerCase()) ||
                                item.data?.patient_name?.toLowerCase().includes(value.toLowerCase()) ||
                                item.name?.toLowerCase().includes(value.toLowerCase()) ||
                                (item.professional?.key &&
                                    professionals[item.professional.key]?.display_name[0].text
                                        .toLowerCase()
                                        .includes(value.toLowerCase())) ||
                                (item.appointment?.doctor_name &&
                                    professionals[item.appointment.doctor_name]?.display_name[0].text
                                        .toLowerCase()
                                        .includes(value.toLowerCase()))
                            )
                        )
                            includesSearch = false;
                    });
                    return includesSearch;
                })
            );
        } else setFilteredAppointments(newFilteredAppointments);
    }, [search, showPastAppointments, showAppointmentsWithoutProfessional, unknownAppointments, professionalsFilter]);

    if (loading) return <LoadingPage />;

    if (hasFailed) return <FailedFallback reloadData={reloadData} />;

    return (
        <CContainer>
            <CContainer
                className="m-0 p-0"
                style={{
                    backdropFilter: 'blur(0.3rem)',
                    zIndex: 5,
                    width: '100%',
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <CForm className="d-flex align-items-center gap-4">
                        <CFormInput
                            style={{ width: '12rem' }}
                            type="text"
                            id="search"
                            value={search}
                            placeholder={t('Search')}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={detailsLoading}
                        />
                        {selectedLocation.dcConfig.is_filtering_active && filteredAndSortedProfessionals.length > 1 && (
                            <div style={{ position: 'relative' }}>
                                <CButton color="secondary" onClick={() => setShowFilterSection(!showFilterSection)}>
                                    <CIcon icon={cilFilter} size="md" />
                                </CButton>
                                {showFilterSection && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            width: '50rem',
                                            top: 0,
                                            left: '50%',
                                            transform: 'translate(-25%, 3rem)',
                                        }}
                                    >
                                        <CCard className="mb-3 d-flex align-items-center justify-content-start gap-3 p-0 w-50">
                                            <CCardBody className="d-flex align-items-start w-100 flex-column user-select-none">
                                                <h5 className="m-0 p-0 mb-2">Filter Einstellungen</h5>
                                                <p className="m-0 p-0">Behandler:innen</p>
                                                {filteredAndSortedProfessionals.map((key) => (
                                                    <div key={key} onClick={() => updateProfessionalsFilter(key)}>
                                                        {/* this div wraps the form check to allow clicking in the label as well */}
                                                        <CFormCheck
                                                            label={professionals[key].display_name[0].text}
                                                            checked={professionalsFilter[key]}
                                                            className="form-check-dark text-black"
                                                        />
                                                    </div>
                                                ))}
                                                <div className="w-100 d-flex align-items-center justify-content-between mt-3">
                                                    <CButton
                                                        style={{ width: '7rem' }}
                                                        size="sm"
                                                        color="light"
                                                        className="border-secondary border-2"
                                                        onClick={resetProfessionalsFilter}
                                                    >
                                                        Zurücksetzen
                                                    </CButton>
                                                    <CButton
                                                        style={{ width: '7rem' }}
                                                        size="sm"
                                                        color="secondary"
                                                        onClick={() => setShowFilterSection(false)}
                                                    >
                                                        Fertig
                                                    </CButton>
                                                </div>
                                            </CCardBody>
                                        </CCard>
                                    </div>
                                )}
                            </div>
                        )}
                        <CFormCheck
                            label={t('Past Appointments (today)')}
                            checked={showPastAppointments}
                            onChange={() => setShowPastAppointments(!showPastAppointments)}
                            disabled={detailsLoading}
                            className="form-check-dark text-black"
                        />
                        <CFormCheck
                            label="Termine ohne Professional"
                            checked={showAppointmentsWithoutProfessional}
                            onChange={() =>
                                setShowAppointmentsWithoutProfessional(!showAppointmentsWithoutProfessional)
                            }
                            disabled={detailsLoading}
                            className="form-check-warning"
                        />
                    </CForm>
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        {selectedLocation.dcConfig.is_new_checkin_available && (
                            <PrinterDropdown>
                                <CButton
                                    onClick={() =>
                                        useTodaysAppointmentsStore.setState({
                                            showGlobalManualCheckinModal: true,
                                            usePcsForGlobalManualCheckin: true,
                                        })
                                    }
                                    color="secondary"
                                >
                                    Manueller Auto-Check-In
                                </CButton>
                            </PrinterDropdown>
                        )}
                        <CButton
                            onClick={() =>
                                useTodaysAppointmentsStore.setState({
                                    showGlobalManualCheckinModal: true,
                                    usePcsForGlobalManualCheckin: false,
                                })
                            }
                            color="light"
                            className="border-secondary border-2"
                        >
                            Manueller Check-In
                        </CButton>
                    </div>
                </div>
                <Alert />
                <div className="mt-3" />
            </CContainer>
            <div
                style={{
                    display: 'flex',
                    height: '80vh',
                }}
            >
                <TableContainer
                    component={Paper}
                    style={{
                        width: selectedAppointment ? '60%' : '100%',
                        transition: 'width 0.15s ease',
                        borderRight: '1px solid #ccc',
                    }}
                >
                    <Table aria-label="collapsible table">
                        <TableHead
                            style={{
                                background: 'white',
                            }}
                        >
                            <TableRow>
                                <TableCell>
                                    <b>{t('Time')}</b>
                                </TableCell>
                                <TableCell style={{ minWidth: '11rem' }}>
                                    <b>{t('Patient name')}</b>
                                </TableCell>
                                <TableCell>
                                    <b>{t('Professional')}</b>
                                </TableCell>
                                <TableCell align="right">
                                    <b>{t('Status')}</b>
                                </TableCell>
                                <TableCell>
                                    <b />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {filteredAppointments.map((item) => (
                            <AppointmentEntry
                                key={item.id}
                                appointment={item}
                                patientName={
                                    item.patient?.name
                                        ? `${item.patient?.name.first_name} ${item.patient?.name.last_name}`
                                        : item.name
                                }
                                selectedAppointment={selectedAppointment}
                                updateSelectedAppointment={updateSelectedAppointment}
                                detailsLoading={detailsLoading}
                                professionalName={
                                    professionals[item.professional?.key ?? item.appointment?.doctor_name]
                                        ?.display_name[0].text ||
                                    professionals[checkinData[item.id]?.appointment?.doctor_name]?.display_name[0].text
                                }
                            />
                        ))}
                    </Table>
                </TableContainer>
                {selectedAppointment && (
                    <AppointmentDetails
                        appointment={selectedAppointment}
                        detailsLoading={detailsLoading}
                        setDetailsLoading={setDetailsLoading}
                        patientName={`${selectedAppointment.patient?.name.first_name} ${selectedAppointment.patient?.name.last_name}`}
                        professionalName={
                            professionals[
                                selectedAppointment.professional?.key || selectedAppointment.appointment?.doctor_name
                            ]?.display_name[0].text ||
                            professionals[checkinData[selectedAppointment.id]?.appointment?.doctor_name]
                                ?.display_name[0].text
                        }
                        showNewCheckin={selectedLocation.dcConfig.is_new_checkin_available}
                        showLegacyCheckin={selectedLocation.dcConfig.is_legacy_checkin_available}
                        checkin={startCheckin}
                        confirmCheckin={confirmCheckin}
                        updateCheckin={updateCheckin}
                        startLegacyCheckin={startLegacyCheckin}
                    />
                )}
            </div>

            <LegacyCheckinModal
                startCheckin={startLegacyCheckin}
                showLegacyCheckinModal={showLegacyCheckinModal}
                appointment={selectedAppointment}
            />

            <LegacyManualCheckinModal
                showLegacyManualCheckinModal={showLegacyManualCheckinModal}
                manualCheckinPatientName={manualCheckinPatientName}
                setManualCheckinPatientName={setManualCheckinPatientName}
                startCheckin={startLegacyCheckin}
                appointment={selectedAppointment}
            />

            <ManualCheckinModal
                selectedAppointment={selectedAppointment}
                showNewManualCheckinModal={showNewManualCheckinModal}
                manualCheckinPatientName={manualCheckinPatientName}
                setManualCheckinPatientName={setManualCheckinPatientName}
                manualCheckin={manualCheckin}
            />

            <GlobalManualCheckinModal
                showGlobalManualCheckinModal={showGlobalManualCheckinModal}
                manualCheckinPatientName={manualCheckinPatientName}
                setManualCheckinPatientName={setManualCheckinPatientName}
                globalManualCheckin={globalManualCheckin}
            />
            <CertModal />
            <AppointmentCodeModal />
            <LoadingIndicator />
        </CContainer>
    );
};

export default TodaysAppointments;
